import { useCompany } from "~/hooks/useCompany"
import { cn } from "~/utils/helpers"

import { DeltaPill, formatValue, type Format, type GoodWhen } from "./chatBlockHelpers"

// Unified renderer for "kpis" block data. The same block type carries either a single
// headline number (one item) or a small grid (2–6 items); this component picks the
// layout from items.length.
//
// A legacy single-value shape `{ value, format, ... }` (the old "number" block) is also
// accepted and normalized into a 1-item kpis block so transitional / stored data renders.

interface Period {
  label: string
  from?: string
  to?: string
}

interface Comparison {
  value: number
  period: Period
  delta?: { absolute?: number; percent?: number; goodWhen?: GoodWhen }
}

interface MetricItem {
  label?: string
  value: number
  format: Format
  period?: Period
  comparison?: Comparison
  delta?: { value: number; label: string; goodWhen?: GoodWhen }
}

interface ChatMetricBlockProps {
  validated?: unknown
  /** Drop the outer card chrome. The label is also hidden when empty/missing in 1-item mode. */
  bare?: boolean
}

interface NormalizedBlock {
  items: MetricItem[]
  emphasis?: "hero" | "default"
}

function isMetricItem(x: unknown): x is MetricItem {
  return x != null && typeof x === "object" && typeof (x as MetricItem).value === "number"
}

function normalize(data: unknown): NormalizedBlock | null {
  if (data == null || typeof data !== "object") return null
  const obj = data as Record<string, unknown>
  // Canonical kpis shape: { items: [...] }
  if (Array.isArray(obj.items)) {
    const items = obj.items.filter(isMetricItem)
    if (items.length === 0) return null
    return { items, emphasis: obj.emphasis as "hero" | "default" | undefined }
  }
  // Legacy single-value shape: { value, format, ... } → wrap as a 1-item kpis block.
  if (isMetricItem(data)) {
    const item: MetricItem & { emphasis?: "hero" | "default" } = data
    return { items: [item], emphasis: item.emphasis }
  }
  return null
}

function comparisonPercent(item: MetricItem): { percent: number; goodWhen?: GoodWhen } | null {
  if (item.comparison) {
    const { value, delta } = item.comparison
    const percent =
      delta?.percent ?? (value !== 0 ? ((item.value - value) / Math.abs(value)) * 100 : 0)
    return { percent, goodWhen: delta?.goodWhen }
  }
  if (item.delta) {
    return { percent: item.delta.value, goodWhen: item.delta.goodWhen }
  }
  return null
}

// Pick a column count that produces balanced rows (max 3 to keep labels readable).
// 2→2x1, 3→1x3, 4→2x2, 5→3+2, 6→2x3. Mobile falls back to 2 cols so tight containers stay legible.
const COLS_BY_COUNT: Record<number, string> = {
  2: "grid-cols-2",
  3: "grid-cols-2 md:grid-cols-3",
  4: "grid-cols-2",
  5: "grid-cols-2 md:grid-cols-3",
  6: "grid-cols-2 md:grid-cols-3",
}

export default function ChatMetricBlock({ validated, bare }: ChatMetricBlockProps) {
  const { data: company } = useCompany()
  const currency = company?.currency ?? "EUR"
  const normalized = normalize(validated)

  if (!normalized) {
    return (
      <pre className="overflow-x-auto rounded-lg bg-medium p-3 text-xs">
        <code>Invalid metric block</code>
      </pre>
    )
  }

  const { items, emphasis } = normalized
  const isSingle = items.length === 1
  const isHero = isSingle && emphasis === "hero"

  // Value sizing is container-query-driven so it adapts to tile width (and grid cell
  // width when multi-item).
  const valueSize = isSingle
    ? isHero
      ? "text-[clamp(1.25rem,7cqw,2.25rem)]"
      : "text-[clamp(1.1rem,5.5cqw,1.75rem)]"
    : "text-[clamp(0.95rem,7cqw,1.5rem)]"

  function renderItem(item: MetricItem, key: number) {
    const cmp = comparisonPercent(item)
    const cmpValueText =
      item.comparison &&
      `vs ${item.comparison.period.label}: ${formatValue(item.comparison.value, item.format, currency)}`
    const showItemLabel = !isSingle || (!bare && !!item.label)
    return (
      <div key={key} className="@container flex min-w-0 flex-col">
        {showItemLabel && item.label && (
          <div className="paragraph-sm text-fg-subtle truncate">{item.label}</div>
        )}
        <div
          className={cn(
            // whitespace-nowrap (not truncate) — truncate's overflow:hidden was clipping
            // the top of digits at large clamp() font sizes. The container-query font
            // sizing already prevents horizontal overflow in normal cases.
            "font-semibold leading-tight tabular-nums whitespace-nowrap",
            valueSize,
            showItemLabel && item.label && "mt-2",
          )}
        >
          {formatValue(item.value, item.format, currency)}
        </div>
        {item.period && (
          <div className="mt-2 paragraph-sm text-fg-subtle truncate">{item.period.label}</div>
        )}
        {isSingle && item.comparison && cmp ? (
          // Single-item layout shows the prior-period number + delta inline ("vs Mar 1–30: 1,234 ▲ 12%").
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 paragraph-sm">
            <span className="text-fg-subtle">vs {item.comparison.period.label}:</span>
            <span className="text-fg">
              {formatValue(item.comparison.value, item.format, currency)}
            </span>
            <DeltaPill percent={cmp.percent} goodWhen={cmp.goodWhen} />
          </div>
        ) : isSingle && item.delta ? (
          // Legacy free-text delta path — preserved for back-compat.
          <div className="mt-1 flex flex-wrap items-center gap-1 paragraph-sm">
            <DeltaPill percent={item.delta.value} goodWhen={item.delta.goodWhen} />
            <span className="text-fg-subtle">{item.delta.label}</span>
          </div>
        ) : (
          cmp && (
            <div className="mt-0.5" title={cmpValueText ?? item.delta?.label}>
              <DeltaPill percent={cmp.percent} goodWhen={cmp.goodWhen} />
            </div>
          )
        )}
      </div>
    )
  }

  const body = isSingle ? (
    renderItem(items[0]!, 0)
  ) : (
    <div
      className={cn(
        "grid gap-x-4 gap-y-3",
        COLS_BY_COUNT[items.length] ?? "grid-cols-2 md:grid-cols-3",
      )}
    >
      {items.map((it, i) => renderItem(it, i))}
    </div>
  )

  // `@container` makes this element a container-query root so `cqw` units inside the
  // value class resolve against the tile/message width. `flex-1 justify-center` keeps
  // content vertically centered in tile mode so a short metric doesn't look top-anchored.
  if (bare)
    return <div className="@container flex min-w-0 flex-1 flex-col justify-center">{body}</div>

  return <div className="@container my-2 min-w-0 rounded-lg bg-medium p-4">{body}</div>
}
